import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import airpods from '../assets/icons/airpods.png';
import film from '../assets/icons/film.png';
import experience from '../assets/icons/experience.jpeg';

// Each logo floats in its own fixed, non-overlapping pocket. Nothing orbits —
// the highlight just drifts slowly from one to the next, and a scroll lets the
// visitor move the highlight along at their own pace.
const ITEMS = [
  {
    img: airpods,
    title: 'AirPods Adventure',
    to: '/map',
    pos: { left: '69%', top: '15%' },
    anim: 'animate-drift',
    delay: '0s',
  },
  {
    img: film,
    title: 'Film Photography',
    to: '/ThirtyFiveMM',
    pos: { left: '24%', top: '48%' },
    anim: 'animate-drift-slow',
    delay: '-3s',
  },
  {
    img: experience,
    title: 'Professional Journey',
    to: '/experience',
    pos: { left: '66%', top: '81%' },
    anim: 'animate-drift',
    delay: '-5.5s',
  },
];
const N = ITEMS.length;

const calcDims = () => {
  const mobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return mobile ? { size: 104, box: 380 } : { size: 150, box: 560 };
};

// Hand-drawn squiggly underline.
const Squiggle = ({ className }) => (
  <svg className={className} width="116" height="12" viewBox="0 0 116 12" fill="none" aria-hidden>
    <path
      d="M2 7c13-6 25 4 38 0s25-6 38-1 21 4 36 0"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const NavWheel = () => {
  const navigate = useNavigate();
  const [dims, setDims] = useState(calcDims);
  const [selected, setSelected] = useState(0);

  const selRef = useRef(0);
  const lastInputRef = useRef(0);
  const accRef = useRef(0);
  const wheelAreaRef = useRef(null);

  useEffect(() => {
    const onResize = () => setDims(calcDims());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const step = useCallback((dir) => {
    setSelected((s) => {
      const n = (((s + dir) % N) + N) % N;
      selRef.current = n;
      return n;
    });
  }, []);

  // Idle drift — the highlight moves on by itself on a slow interval.
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = setInterval(() => {
      if (performance.now() - lastInputRef.current < 2400) return;
      step(1);
    }, 3200);
    return () => clearInterval(id);
  }, [step]);

  // Scroll moves the highlight at the visitor's own speed.
  useEffect(() => {
    const onWheel = (e) => {
      e.preventDefault();
      lastInputRef.current = performance.now();
      accRef.current += e.deltaY;
      const STEP = 220;
      while (accRef.current >= STEP) {
        accRef.current -= STEP;
        step(1);
      }
      while (accRef.current <= -STEP) {
        accRef.current += STEP;
        step(-1);
      }
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [step]);

  // Touch drag does the same on mobile.
  useEffect(() => {
    let lastY = null;
    let acc = 0;
    const onStart = (e) => {
      lastY = e.touches[0].clientY;
      acc = 0;
      lastInputRef.current = performance.now();
    };
    const onMove = (e) => {
      if (lastY == null) return;
      lastInputRef.current = performance.now();
      const y = e.touches[0].clientY;
      acc += lastY - y;
      lastY = y;
      const STEP = 64;
      while (acc >= STEP) {
        acc -= STEP;
        step(1);
      }
      while (acc <= -STEP) {
        acc += STEP;
        step(-1);
      }
    };
    const onEnd = () => {
      lastY = null;
    };
    const el = wheelAreaRef.current;
    if (!el) return undefined;
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [step]);

  const { size, box } = dims;

  return (
    <div ref={wheelAreaRef} className="relative" style={{ width: box, height: box }}>
      {ITEMS.map((it, i) => {
        const isSel = selected === i;
        return (
          <div
            key={it.to}
            className="absolute"
            style={{ left: it.pos.left, top: it.pos.top, transform: 'translate(-50%, -50%)' }}
          >
            <button
              onClick={() => navigate(it.to)}
              aria-label={`Enter ${it.title}`}
              className={`${it.anim} flex cursor-pointer flex-col items-center text-center`}
              style={{ width: size, animationDelay: it.delay }}
            >
              <span
                className="block transition-transform duration-500 ease-out"
                style={{ transform: isSel ? 'scale(1.12)' : 'scale(0.82)' }}
              >
                <img
                  src={it.img}
                  alt={it.title}
                  draggable={false}
                  className="object-contain transition-opacity duration-500"
                  style={{
                    width: size,
                    height: size,
                    opacity: isSel ? 1 : 0.45,
                    filter: 'drop-shadow(0 18px 28px rgba(0,66,37,0.3))',
                  }}
                />
              </span>

              <span
                className={`mt-4 max-w-[12rem] uppercase leading-[0.95] tracking-tightest transition-all duration-500 ${
                  isSel
                    ? 'font-bold text-ink text-lg sm:text-2xl'
                    : 'font-semibold text-ink/35 text-xs sm:text-sm'
                }`}
              >
                {it.title}
              </span>

              <span
                className="mt-1 h-3 text-ink transition-opacity duration-500"
                style={{ opacity: isSel ? 1 : 0 }}
              >
                <Squiggle className="text-ink" />
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default NavWheel;
